<!-- components/courses/CourseCard.vue -->
<template>
  <div class="course-card" @click="$emit('click')">
    <div class="thumbnail-wrapper">
      <img
        v-if="course.thumbnail"
        :src="course.thumbnail"
        :alt="course.title"
        loading="lazy"
      />
      <div v-else class="thumbnail-placeholder">
        <span>📚</span>
      </div>

      <div class="thumbnail-overlay">
        <span v-if="course.isNew" class="badge new">NEW</span>
        <span v-if="course.isTrending" class="badge trending">🔥</span>
        <span v-if="course.isPublished === false" class="badge draft"
          >DRAFT</span
        >
      </div>
    </div>

    <div class="course-info">
      <h3 class="title">{{ course.title }}</h3>

      <div class="creator-info" @click.stop="goToCreator">
        <img
          :src="
            course.creatorAvatar ||
            `https://ui-avatars.com/api/?name=${course.creatorName}&background=FF8000&color=fff`
          "
          :alt="course.creatorName"
        />
        <span>{{ course.creatorName }}</span>
      </div>

      <div class="course-meta">
        <div class="rating">
          <span class="stars">⭐</span>
          <span>{{ course.rating?.toFixed(1) || "New" }}</span>
          <span class="students"
            >({{ formatNumber(course.totalStudents || 0) }})</span
          >
        </div>
        <span class="price">{{
          course.price > 0 ? `$${course.price.toFixed(2)}` : "Free"
        }}</span>
      </div>

      <div
        v-if="showProgress && course.progress !== undefined"
        class="progress-bar"
      >
        <div class="progress-track">
          <div
            class="progress-fill"
            :style="{ width: course.progress + '%' }"
          ></div>
        </div>
        <span class="progress-text">{{ course.progress }}% complete</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from "vue";
import { useRouter } from "vue-router";

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
  showProgress: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["click"]);
const router = useRouter();

const formatNumber = (num) => {
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num;
};

const goToCreator = (e) => {
  e.stopPropagation();
  router.push(`/creator/${props.course.creatorId}`);
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;

.course-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

    .thumbnail-overlay {
      opacity: 1;
    }
  }

  .thumbnail-wrapper {
    position: relative;
    padding-top: 56.25%;
    background: #f1f3f5;
    overflow: hidden;

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .thumbnail-placeholder {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #f8f9fa, #e9ecef);

      span {
        font-size: 48px;
      }
    }

    .thumbnail-overlay {
      position: absolute;
      top: 8px;
      right: 8px;
      display: flex;
      gap: 6px;
      opacity: 0.8;
      transition: opacity 0.3s ease;

      .badge {
        padding: 2px 10px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 700;
        color: #fff;

        &.new {
          background: rgba(40, 167, 69, 0.9);
        }

        &.trending {
          background: rgba(255, 128, 0, 0.9);
        }

        &.draft {
          background: rgba(108, 117, 125, 0.9);
        }
      }
    }
  }

  .course-info {
    padding: 14px;

    .title {
      font-size: 15px;
      font-weight: 600;
      color: #1a1a1a;
      margin: 0 0 8px 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-height: 1.4;
      min-height: 42px;
    }

    .creator-info {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
      padding: 4px 0;
      cursor: pointer;

      img {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        object-fit: cover;
      }

      span {
        font-size: 13px;
        color: #495057;
        transition: color 0.2s ease;

        &:hover {
          color: $primary-orange;
        }
      }
    }

    .course-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .rating {
        font-size: 14px;
        color: #495057;

        .stars {
          margin-right: 4px;
        }

        .students {
          font-size: 13px;
          color: #868e96;
          margin-left: 4px;
        }
      }

      .price {
        font-weight: 700;
        color: $primary-orange;
        font-size: 16px;
      }
    }

    .progress-bar {
      margin-top: 12px;

      .progress-track {
        width: 100%;
        height: 4px;
        background: #e9ecef;
        border-radius: 2px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background: $primary-orange;
          border-radius: 2px;
          transition: width 0.3s ease;
        }
      }

      .progress-text {
        display: block;
        font-size: 12px;
        color: #868e96;
        margin-top: 4px;
      }
    }
  }
}
</style>
